import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import moment from "moment";
import { useAuth } from "../../../provider/AuthProvider.tsx";
import SectionHeader from "../../../components/SectionHeader/SectionHeader.tsx";
import Breadcrumb from "../../../components/Breadcrumbs/Breadcrumb.tsx";
import DatePicker from "../../../components/Forms/DatePicker/DatePicker.tsx";
import FadeAlert, { FadeAlertType } from "../../../components/FadeAlert/FadeAlert.tsx";
import SelectGroup from "../../../components/Forms/SelectGroup/SelectGroup.tsx";
import { labelClass } from "../../../constants/formStyles.ts";
import type { CalcNavigationState } from "../../../types/calculatorTypes.ts";
import {
  GeneraliIntegrationService,
  type GeneraliOfferResponse,
} from "../../../services/Integrations/GeneraliIntegration.ts";

// ─── Constants ────────────────────────────────────────────────────────────────

// Generali's own VehiclePurpose nomenclature, id-for-name.
// Source of truth: wing-insurers-api/examples/generali/data/VehiclePurpose.json
// (mirrored by docs/providers/samples/responses/generali_VehiclePurposes.json).
//
// Three of the five previous entries were mislabelled against that list and sent a
// purpose the broker did not choose: "Такси" sent 3 (ЛИЗИНГ), "Търговски цели" sent
// 8 (ОПАСНИ ТОВАРИ), and "Изпитателен" sent 5 - which is in fact ТАКСИ. Selecting
// "Такси" therefore priced the policy as leasing. Do not edit an id or a label here
// without re-checking it against that file.
const VEHICLE_PURPOSE_OPTIONS: Record<string, string> = {
  "1": "Лични нужди",
  "3": "Лизинг",
  "4": "Отдаване под наем (рент-а-кар)",
  "5": "Такси",
  "6": "Учебен автомобил",
  "8": "Опасни товари",
  "9": "Състезателна дейност",
  "11": "Международен транспорт",
  "12": "Куриерски услуги",
  "13": "Производствена дейност",
  "14": "Търговска дейност",
  "15": "Строителна дейност",
  "16": "Друга фирмена дейност",
  "17": "МПС със специален режим на движение",
  "18": "Друго предназначение",
  "19": "Охранителна и детективска дейност",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col">
    <span className="text-body dark:text-bodydark text-[11px]">{label}</span>
    <span className="text-xs font-semibold text-black dark:text-white">
      {value ?? "-"}
    </span>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const Generali: React.FC = () => {
  const { jwt } = useAuth();
  const location = useLocation();
  const calcState = location.state as CalcNavigationState | undefined;

  const today = moment().add(1, "day").format("YYYY-MM-DD");

  const [form, setForm] = useState(() => ({
    vehiclePurpose: "1",
    isRightWheelDrive: false,
    isInsurerForeigner: false,
    // Generali's legal code is "3" (not "2"); prefer the backend-mapped value.
    insurerType:
      calcState?.holder?.providers?.generali?.insurerType ??
      (calcState?.owner?.personType === "2" ? "3" : "1"),
    commencingDate: today,
    installmentsCount: 1,
  }));

  const [offerLoading, setOfferLoading] = useState(false);
  const [offerResult, setOfferResult] = useState<GeneraliOfferResponse | null>(null);
  const [issuedPolicyNo, setIssuedPolicyNo] = useState<string | null>(null);
  const [issuedTransactionId, setIssuedTransactionId] = useState<string | null>(null);
  const [printLoading, setPrintLoading] = useState(false);
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>(FadeAlertType.info);

  const autoCalcRef = useRef(false);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  // ── Get offer ──────────────────────────────────────────────────────────────

  const getOffer = async () => {
    if (!calcState?.vehicle || !calcState?.owner) {
      setAlert("Липсват данни. Моля, върнете се към калкулатора.");
      setAlertType(FadeAlertType.error);
      return;
    }
    setOfferLoading(true);
    setOfferResult(null);
    setIssuedPolicyNo(null);
    setIssuedTransactionId(null);
    try {
      const result = await GeneraliIntegrationService.getOffer(jwt, {
        VehiclePurpose: form.vehiclePurpose,
        IsRightWheelDrive: form.isRightWheelDrive,
        IsInsurerForeigner: form.isInsurerForeigner,
        InsurerType: form.insurerType,
        Pin: calcState.owner.pid,
        CommencingDate: `${form.commencingDate}T00:00:00`,
        RegistrationPlate: calcState.vehicle.regNo,
        CertificateNumber: calcState.vehicle.certNumber,
        InstallmentsCount: form.installmentsCount,
      });
      setOfferResult(result);
    } catch (e: any) {
      setAlert(e.message ?? "Грешка при получаване на оферта");
      setAlertType(FadeAlertType.error);
    } finally {
      setOfferLoading(false);
    }
  };

  // ── Issue policy ───────────────────────────────────────────────────────────

  const issuePolicy = async () => {
    const offerId = offerResult?.OfferDetails?.[0]?.OfferId;
    if (!offerId) {
      setAlert("Няма валидна оферта за издаване.");
      setAlertType(FadeAlertType.error);
      return;
    }
    try {
      const result = await GeneraliIntegrationService.issuePolicy(jwt, { OfferId: offerId });
      const transactionId = result?.TransactionId ?? null;
      const policyNo = result?.PolicyNumber ?? transactionId;
      if (!policyNo) {
        throw new Error("Отговорът от Дженерали не съдържа номер на полица.");
      }
      setIssuedPolicyNo(policyNo);
      setIssuedTransactionId(transactionId);
      setAlert(`Полицата е издадена успешно. № ${policyNo}`);
      setAlertType(FadeAlertType.success);
    } catch (e: any) {
      setAlert(e.message ?? "Грешка при издаване на полица");
      setAlertType(FadeAlertType.error);
    }
  };

  // ── Print ──────────────────────────────────────────────────────────────────

  const printPolicy = async () => {
    // The print endpoint is keyed by transaction ID, not policy number
    const printId = issuedTransactionId ?? issuedPolicyNo;
    if (!printId) return;
    setPrintLoading(true);
    try {
      const blob = await GeneraliIntegrationService.printTransaction(jwt, printId);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e: any) {
      setAlert(e.message ?? "Грешка при печат");
      setAlertType(FadeAlertType.error);
    } finally {
      setPrintLoading(false);
    }
  };

  // Auto-get offer when arriving from calculator
  useEffect(() => {
    if (!calcState || autoCalcRef.current) return;
    autoCalcRef.current = true;
    const timer = setTimeout(() => getOffer(), 300);
    return () => clearTimeout(timer);
  }, []);

  // ── Derived ────────────────────────────────────────────────────────────────

  const offerDetail = offerResult?.OfferDetails?.[0];

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <>
      <Breadcrumb pageName="Дженерали - Гражданска отговорност" />

      <FadeAlert alert={alert} type={alertType} onClose={() => setAlert(null)} />

      {/* ── Summary from calculator ── */}
      {calcState && (
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark rounded-sm border bg-white">
            <SectionHeader title="Данни за МПС" />
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 p-3 pt-2">
              <InfoRow label="Рег. номер" value={calcState.vehicle.regNo} />
              <InfoRow label="№ Свидетелство" value={calcState.vehicle.certNumber} />
              <InfoRow label="Марка / Модел" value={`${calcState.vehicle.make} ${calcState.vehicle.model}`.trim()} />
              <InfoRow label="Год. производство" value={calcState.vehicle.productionYear || "-"} />
            </div>
          </fieldset>
          <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark rounded-sm border bg-white">
            <SectionHeader title="Данни за клиент" />
            <div className="grid grid-cols-1 gap-y-2 p-3 pt-2">
              <div className="mb-1 flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#cc0000]/10 text-sm font-bold text-[#cc0000]">
                  {calcState.owner.name.split(" ").slice(0, 2).map((w) => w[0]).join("")}
                </div>
                <div>
                  <p className="text-sm font-bold text-black dark:text-white">{calcState.owner.name}</p>
                  <p className="text-body dark:text-bodydark text-[11px]">
                    {calcState.owner.personType === "1" ? "Физическо лице" : "Юридическо лице"}
                  </p>
                </div>
              </div>
              <InfoRow label={calcState.owner.personType === "1" ? "ЕГН" : "ЕИК"} value={calcState.owner.pid} />
            </div>
          </fieldset>
        </div>
      )}

      {/* ── Policy parameters ── */}
      <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark mb-4 rounded-sm border bg-white">
        <SectionHeader title="Параметри на полицата - Дженерали" />
        <div className="grid grid-cols-1 gap-3 p-4 pt-2 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className={labelClass}>Начало</label>
            <DatePicker
              name="commencingDate"
              value={moment(form.commencingDate).format("DD-MM-YYYY")}
              onChange={(e) =>
                set("commencingDate", moment(e.target.value, "DD-MM-YYYY").format("YYYY-MM-DD"))
              }
            />
          </div>
          <div>
            <label className={labelClass}>Брой вноски</label>
            <SelectGroup
              value={String(form.installmentsCount)}
              onChange={(v) => set("installmentsCount", Number(v))}
              options={{ "1": "1 вноска", "2": "2 вноски", "4": "4 вноски", "12": "12 вноски" }}
            />
          </div>
          <div>
            <label className={labelClass}>Ползване на МПС</label>
            <SelectGroup
              value={form.vehiclePurpose}
              onChange={(v) => set("vehiclePurpose", v)}
              options={VEHICLE_PURPOSE_OPTIONS}
            />
          </div>
          <div>
            <label className={labelClass}>Тип застраховащ</label>
            <SelectGroup
              value={form.insurerType}
              onChange={(v) => set("insurerType", v)}
              options={{ "1": "Физическо лице", "2": "Юридическо лице" }}
            />
          </div>
          <div className="flex items-end gap-2 pb-1">
            <input
              type="checkbox"
              id="rightHandDrive"
              checked={form.isRightWheelDrive}
              onChange={(e) => set("isRightWheelDrive", e.target.checked)}
              className="h-4 w-4"
            />
            <label htmlFor="rightHandDrive" className={labelClass + " mb-0 cursor-pointer"}>
              Десен волан
            </label>
          </div>
          <div className="flex items-end gap-2 pb-1">
            <input
              type="checkbox"
              id="foreigner"
              checked={form.isInsurerForeigner}
              onChange={(e) => set("isInsurerForeigner", e.target.checked)}
              className="h-4 w-4"
            />
            <label htmlFor="foreigner" className={labelClass + " mb-0 cursor-pointer"}>
              Чуждестранно лице (ЛНЧ)
            </label>
          </div>
        </div>

        <div className="flex justify-center gap-3 px-4 pb-4">
          <button
            onClick={getOffer}
            disabled={offerLoading}
            className="flex items-center gap-2 rounded bg-[#cc0000] px-6 py-2 text-sm font-semibold text-white hover:bg-opacity-90 disabled:opacity-50"
          >
            {offerLoading && (
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            )}
            {offerLoading ? "Зарежда..." : "Вземи оферта"}
          </button>

          {offerDetail?.OfferId && !issuedPolicyNo && (
            <button
              onClick={issuePolicy}
              className="rounded bg-green-600 px-6 py-2 text-sm font-semibold text-white hover:bg-opacity-90"
            >
              Издай полица
            </button>
          )}

          {issuedPolicyNo && (
            <button
              onClick={printPolicy}
              disabled={printLoading}
              className="flex items-center gap-2 rounded bg-[#243095] px-6 py-2 text-sm font-semibold text-white hover:bg-opacity-90 disabled:opacity-50"
            >
              {printLoading ? "Зарежда PDF..." : "Печат"}
            </button>
          )}
        </div>
      </fieldset>

      {/* ── Offer result ── */}
      {offerDetail && (
        <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark rounded-sm border bg-white">
          <SectionHeader title="Оферта от Дженерали" />
          <div className="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-sm bg-[#cc0000]/10 px-6 py-4 text-center">
                <p className="text-body text-[11px] uppercase tracking-wide">Обща дължима сума</p>
                <p className="text-2xl font-bold text-[#cc0000]">
                  {offerDetail.TotalDueAmount?.toFixed(2)} EUR
                </p>
                {offerDetail.InstallmentsCount && offerDetail.InstallmentsCount > 1 && (
                  <p className="text-body text-xs">
                    {(offerDetail.InstallmentAmounts?.[0]?.Amount ??
                      offerDetail.TotalDueAmount / offerDetail.InstallmentsCount
                    ).toFixed(2)} EUR / вноска × {offerDetail.InstallmentsCount}
                  </p>
                )}
              </div>
              {offerDetail.OfferId && (
                <div>
                  <p className="text-body text-xs">Offer ID</p>
                  <p className="font-mono text-xs text-black dark:text-white">{offerDetail.OfferId}</p>
                </div>
              )}
            </div>
          </div>
        </fieldset>
      )}
    </>
  );
};

export default Generali;

